import { useState, type FormEvent } from "react";
import { fmtDate, fmtDateTime, fmtInt } from "@/components/admin/format";
import { LoadState, PageHead } from "@/components/admin/PageHead";
import { useLoad } from "@/components/admin/useLoad";
import { errorMessage } from "@/components/auth/issues";
import { Button, ButtonLink, TextField } from "@/components/ui/FormControls";
import { InfoTip } from "@/components/ui/InfoTip";
import { api, apiUrl } from "@/lib/api";
import { useAuth } from "@/store/auth";

interface Row {
  id: string;
  email: string;
  name: string;
  organization: string;
  department: string | null;
  jobTitle: string | null;
  phone: string;
  role: "learner" | "admin";
  consentMarketingAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  scenarios: number;
}
interface Page {
  total: number;
  page: number;
  size: number;
  users: Row[];
}

const SIZE = 50;

/** 가입자 명단 , 영업 파이프라인 자료. 마케팅 동의 열이 핵심이라 앞쪽에 세운다 */
export function UsersPage() {
  const me = useAuth((s) => s.user);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const path = `/admin/users?page=${page}&size=${SIZE}${q ? `&search=${encodeURIComponent(q)}` : ""}`;
  const { data, error, loading, reload } = useLoad<Page>(path);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [note, setNote] = useState<{ kind: "ok" | "danger"; text: string } | null>(null);

  function submitSearch(e: FormEvent) {
    e.preventDefault();
    setQ(search.trim());
    setPage(1);
  }

  async function changeRole(u: Row) {
    const next = u.role === "admin" ? "learner" : "admin";
    if (u.id === me?.id && next === "learner" && !window.confirm("자기 자신의 관리자 권한을 내립니다. 이 화면에서 바로 나가게 됩니다. 계속할까요?")) return;
    setNote(null);
    setBusyId(u.id);
    try {
      await api(`/admin/users/${u.id}`, { method: "PATCH", json: { role: next } });
      setNote({ kind: "ok", text: `${u.email} → ${next === "admin" ? "관리자" : "학습자"}` });
      reload();
    } catch (e) {
      setNote({ kind: "danger", text: errorMessage(e) });
    } finally {
      setBusyId(null);
    }
  }

  async function remove(u: Row) {
    if (!window.confirm(`${u.email} 계정과 저장된 시나리오 ${u.scenarios}개를 모두 삭제합니다. 되돌릴 수 없습니다.`)) return;
    setNote(null);
    setBusyId(u.id);
    try {
      await api(`/admin/users/${u.id}`, { method: "DELETE" });
      setNote({ kind: "ok", text: `${u.email} 계정을 삭제했습니다.` });
      reload();
    } catch (e) {
      setNote({ kind: "danger", text: errorMessage(e) });
    } finally {
      setBusyId(null);
    }
  }

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / SIZE));
  const from = total ? (page - 1) * SIZE + 1 : 0;
  const to = Math.min(total, page * SIZE);

  return (
    <div className="admin-page">
      <PageHead
        title="가입자"
        desc={
          <>
            영업 파이프라인 자료입니다. 교육 및 서비스 안내와 영업 연락은 <b className="font-semibold text-ink">마케팅 동의</b>가 있는 사람에게만 합니다.
          </>
        }
        aside={
          <ButtonLink variant="soft" href={apiUrl("/admin/users.csv")}>
            CSV 내려받기
          </ButtonLink>
        }
      />

      <form onSubmit={submitSearch} className="flex flex-wrap items-end gap-item">
        <div className="w-full max-w-form">
          <TextField label="검색" type="search" placeholder="이메일, 이름, 회사" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Button type="submit" variant="secondary">검색</Button>
        {q && (
          <Button
            variant="secondary"
            onClick={() => {
              setSearch("");
              setQ("");
              setPage(1);
            }}
          >
            지우기
          </Button>
        )}
      </form>

      {note && (
        <p role={note.kind === "danger" ? "alert" : "status"} className={`note note-${note.kind} text-label`}>
          {note.text}
        </p>
      )}
      <LoadState loading={loading && !data} error={error} empty={!!data && data.users.length === 0} emptyText={q ? "검색 결과가 없습니다." : "아직 가입자가 없습니다."} />

      {data && data.users.length > 0 && (
        <div className="table-scroll admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>이메일</th>
                <th>이름</th>
                <th>회사 또는 기관</th>
                <th>부서 / 직책</th>
                <th>연락처</th>
                <th>마케팅 동의</th>
                <th className="cell-num">시나리오</th>
                <th>가입일</th>
                <th>마지막 로그인</th>
                <th>
                  <span className="inline-flex items-center gap-label">
                    역할
                    <InfoTip label="역할 규칙">관리자 권한은 계정에 저장되며, 역할을 내리면 관리자 API 접근이 즉시 차단됩니다.</InfoTip>
                  </span>
                </th>
                <th>동작</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.id}>
                  <td className="cell-key">{u.email}</td>
                  <td className="cell-nowrap">{u.name}</td>
                  <td>{u.organization}</td>
                  <td className="cell-muted">{[u.department, u.jobTitle].filter(Boolean).join(" / ") || "없음"}</td>
                  <td className="cell-nowrap font-mono">{u.phone}</td>
                  <td className="cell-nowrap">
                    {u.consentMarketingAt ? (
                      <>
                        <span className="badge-accent">동의</span> <span className="cell-muted text-footnote">{fmtDate(u.consentMarketingAt)}</span>
                      </>
                    ) : (
                      <span className="badge-muted">미동의</span>
                    )}
                  </td>
                  <td className="cell-num">{fmtInt(u.scenarios)}</td>
                  <td className="cell-nowrap cell-muted">{fmtDate(u.createdAt)}</td>
                  <td className="cell-nowrap cell-muted">{fmtDateTime(u.lastLoginAt)}</td>
                  <td className="cell-nowrap">{u.role === "admin" ? <span className="badge-ok">관리자</span> : <span className="cell-muted">학습자</span>}</td>
                  <td>
                    <div className="row-actions">
                      <Button size="compact" disabled={busyId === u.id} onClick={() => void changeRole(u)}>
                        {u.role === "admin" ? "학습자로" : "관리자로"}
                      </Button>
                      <Button variant="danger" size="compact" disabled={busyId === u.id || u.id === me?.id} onClick={() => void remove(u)}>
                        삭제
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && total > 0 && (
        <div className="pager">
          <span>
            <span className="font-mono">{fmtInt(from)}부터 {fmtInt(to)}까지</span> / <span className="font-mono">{fmtInt(total)}</span>명{q && `, "${q}" 검색`}
          </span>
          <div className="row-actions">
            <Button size="compact" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>이전</Button>
            <span className="font-mono">
              {page} / {pages}
            </span>
            <Button size="compact" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>다음</Button>
          </div>
        </div>
      )}

      <p className="text-footnote leading-relaxed text-ink-subtle">
        개인정보 수집 및 이용 동의(필수)는 교육 운영, 수료 관리, 문의 대응 목적에만 씁니다. 마케팅 동의가 없는 사람의 연락처를 영업 및 안내에 쓰면 동의 범위를 넘습니다. CSV에는 동의 일시가 함께 나갑니다.
      </p>
    </div>
  );
}
